import {
  Component,
  OnInit,
  OnDestroy,
  ViewChild,
  TemplateRef,
  inject,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  AfterViewInit
} from '@angular/core';
import { CommonModule } from '@angular/common';
import {
  FormBuilder,
  FormGroup,
  Validators,
  FormsModule,
  ReactiveFormsModule
} from '@angular/forms';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { MatCardModule } from '@angular/material/card';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatTableModule, MatTableDataSource } from '@angular/material/table';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { MatPaginatorModule, MatPaginator } from '@angular/material/paginator';
import { RouterModule } from '@angular/router';
import { Store } from '@ngrx/store';
import { BehaviorSubject, Subject, combineLatest, take, takeUntil } from 'rxjs';
import { map } from 'rxjs/operators';
import {
  ModalidadSeleccion,
  CreateModalidadSeleccionRequest,
  UpdateModalidadSeleccionRequest
} from '../../models/modalidad-seleccion.interface';
import { MODALIDAD_SELECCION_LABELS } from './modalidad-seleccion.labels';
import * as ModalidadSeleccionActions from '../../store/modalidad-seleccion.actions';
import * as ModalidadSeleccionSelectors from '../../store/modalidad-seleccion.selectors';
import { ErrorHandlerService } from '../../../../shared/services/error-handler.service';
import { NotificationService } from '../../../../shared/services/notification.service';
import { LoggerService } from '../../../../core/services/logger.service';
import { TrackByUtils } from '../../../../shared/utils/trackby.utils';

@Component({
  selector: 'app-modalidad-seleccion',
  templateUrl: './modalidad-seleccion.component.html',
  styleUrls: ['./modalidad-seleccion.component.scss'],
  standalone: true,
  changeDetection: ChangeDetectionStrategy.Default,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    MatDialogModule,
    MatCardModule,
    MatButtonModule,
    MatIconModule,
    MatTableModule,
    MatFormFieldModule,
    MatInputModule,
    MatProgressSpinnerModule,
    MatTooltipModule,
    MatSlideToggleModule,
    MatPaginatorModule,
    RouterModule
  ]
})
export class ModalidadSeleccionComponent implements OnInit, OnDestroy, AfterViewInit {
  private readonly store = inject(Store);
  private readonly dialog = inject(MatDialog);
  private readonly formBuilder = inject(FormBuilder);
  private readonly errorHandler = inject(ErrorHandlerService);
  private readonly notificationService = inject(NotificationService);
  private readonly logger = inject(LoggerService);
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly destroy$ = new Subject<void>();

  readonly labels = MODALIDAD_SELECCION_LABELS;

  // Observables del store
  modalidadesSeleccion$ = this.store.select(
    ModalidadSeleccionSelectors.selectAllModalidadesSeleccion
  );
  loading$ = this.store.select(ModalidadSeleccionSelectors.selectModalidadSeleccionLoading);
  error$ = this.store.select(ModalidadSeleccionSelectors.selectModalidadSeleccionError);
  creating$ = this.store.select(ModalidadSeleccionSelectors.selectModalidadSeleccionCreating);
  updating$ = this.store.select(ModalidadSeleccionSelectors.selectModalidadSeleccionUpdating);

  // Filtros por columnas
  columnFilters: { codigo: string; status: string } = {
    codigo: '',
    status: ''
  };

  private readonly filtersSubject = new BehaviorSubject<number>(0);
  filters$ = this.filtersSubject.asObservable();

  // Data source para la tabla con paginación
  dataSource = new MatTableDataSource<ModalidadSeleccion>([]);

  // Subject para manejar los datos de la tabla
  private tableDataSubject = new BehaviorSubject<ModalidadSeleccion[]>([]);
  tableData$ = this.tableDataSubject.asObservable();

  // Observable combinado para mostrar modalidades aplicando búsqueda y filtros por columna
  displayModalidades$ = combineLatest([this.modalidadesSeleccion$, this.filters$]).pipe(
    map(([modalidades]) => {
      let result = modalidades;

      // Búsqueda general
      const globalQuery = (this.searchQuery || '').toLowerCase().trim();
      if (globalQuery) {
        result = result.filter(
          m =>
            m.codigo.toLowerCase().includes(globalQuery) ||
            (m.caracteristicas && m.caracteristicas.toLowerCase().includes(globalQuery))
        );
      }

      // Filtros por columna
      const { codigo, status } = this.columnFilters;
      if (codigo && codigo.trim()) {
        const q = codigo.toLowerCase().trim();
        result = result.filter(m => m.codigo.toLowerCase().includes(q));
      }
      if (status && status.trim()) {
        const q = status.toLowerCase().trim();
        result = result.filter(m => (m.estado ? 'activo' : 'inactivo').includes(q));
      }

      return result;
    })
  );

  // Propiedades para paginación
  pageSize = 10;
  pageSizeOptions = [5, 10, 25, 50];

  // Formularios
  modalidadSeleccionForm!: FormGroup;

  // Estados del componente
  showSearchPanel = false;
  searchQuery = '';
  isEditing = false;
  editingModalidadSeleccion: ModalidadSeleccion | null = null;
  detailBeingViewed: ModalidadSeleccion | null = null;

  // Estados para rastrear operaciones
  wasCreating = false;
  wasUpdating = false;

  // Tabla
  tableColumns: string[] = ['id', 'codigo', 'caracteristicas', 'status', 'actions'];
  trackByModalidadId = TrackByUtils.trackByProperty<ModalidadSeleccion>('modalidadSelId');

  // Referencias a templates
  @ViewChild('createModalidadSeleccionModal') createModalidadSeleccionModal!: TemplateRef<unknown>;
  @ViewChild('detailModal') detailModal!: TemplateRef<unknown>;
  @ViewChild('paginator') paginator!: MatPaginator;

  ngOnInit(): void {
    this.initializeForms();
    this.setupPagination();
    this.loadModalidadesSeleccion();
    this.subscribeToError();
    this.filtersSubject.next(0);

    // Suscripción directa para debuggear cambios en modalidades
    this.modalidadesSeleccion$
      .pipe(
        take(10) // Tomar los primeros 10 cambios
      )
      .subscribe(() => {
        this.cdr.detectChanges();
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  /**
   * Configura la paginación
   */
  private setupPagination(): void {
    // Suscribirse a los cambios en displayModalidades$ para actualizar el data source
    this.displayModalidades$.subscribe(modalidades => {
      this.logger.debug('Updating data source with modalidades', { count: modalidades.length });

      // Actualizar el data source existente
      this.dataSource.data = modalidades;

      // Asegurar que el paginador esté conectado
      if (this.paginator && !this.dataSource.paginator) {
        this.dataSource.paginator = this.paginator;
      }

      // Actualizar el subject para mantener la sincronización
      this.tableDataSubject.next(modalidades);

      // Forzar la detección de cambios
      this.cdr.detectChanges();

      this.logger.debug('Data source updated', {
        dataLength: this.dataSource.data.length,
        paginatorConnected: !!this.dataSource.paginator,
        paginatorLength: this.paginator?.length
      });

      // Verificar el estado del paginador después de actualizar los datos
      setTimeout(() => this.checkPaginatorState(), 100);
    });
  }

  /**
   * Se ejecuta después de que se inicializa la vista
   */
  ngAfterViewInit(): void {
    // Conectar el paginador al data source inmediatamente
    this.dataSource.paginator = this.paginator;

    // Asegurar que el paginador esté configurado correctamente
    if (this.paginator) {
      this.paginator.pageSize = this.pageSize;
      this.paginator.pageSizeOptions = this.pageSizeOptions;
    }

    // Forzar la detección de cambios para asegurar que el paginador se conecte
    this.cdr.detectChanges();

    // Log para debug
    this.logger.debug('ngAfterViewInit - Paginator connected', {
      paginatorExists: !!this.paginator,
      dataSourcePaginator: !!this.dataSource.paginator,
      dataLength: this.dataSource.data.length,
      pageSize: this.paginator?.pageSize,
      pageSizeOptions: this.paginator?.pageSizeOptions
    });
  }

  /**
   * Maneja los cambios de página
   */
  onPageChange(): void {
    // El paginador se maneja automáticamente por el MatTableDataSource
    // Este método puede usarse para logging o acciones adicionales
    this.logger.debug('Page changed', {
      pageIndex: this.paginator?.pageIndex,
      pageSize: this.paginator?.pageSize,
      length: this.paginator?.length,
      dataSourceLength: this.dataSource.data.length,
      filteredDataLength: this.dataSource.filteredData.length
    });
  }

  /**
   * Verifica el estado del paginador
   */
  private checkPaginatorState(): void {
    this.logger.debug('Paginator state check', {
      paginatorExists: !!this.paginator,
      dataSourcePaginator: !!this.dataSource.paginator,
      dataSourceDataLength: this.dataSource.data.length,
      paginatorLength: this.paginator?.length,
      paginatorPageSize: this.paginator?.pageSize,
      paginatorPageIndex: this.paginator?.pageIndex
    });

    // Si el paginador no está conectado, intentar reconectarlo
    if (this.paginator && !this.dataSource.paginator) {
      this.logger.debug('Reconnecting paginator to data source');
      this.dataSource.paginator = this.paginator;
      this.cdr.detectChanges();
    }
  }

  /**
   * Aplica filtros y resetea la paginación
   */
  applyFilters(): void {
    this.filtersSubject.next(Date.now());
    // Reset pagination to first page when filters change
    if (this.paginator) {
      this.paginator.firstPage();
    }
  }

  /**
   * Limpia los filtros y resetea la paginación
   */
  clearFilters(): void {
    this.searchQuery = '';
    this.columnFilters = { codigo: '', status: '' };
    this.filtersSubject.next(Date.now());
    // Reset pagination to first page when filters change
    if (this.paginator) {
      this.paginator.firstPage();
    }
  }

  private initializeForms(): void {
    this.modalidadSeleccionForm = this.formBuilder.group({
      codigo: ['', [Validators.required, Validators.minLength(2), Validators.maxLength(50)]],
      caracteristicas: [
        '',
        [Validators.required, Validators.minLength(5), Validators.maxLength(500)]
      ],
      estado: [true]
    });
  }

  loadModalidadesSeleccion(): void {
    this.store.dispatch(ModalidadSeleccionActions.loadModalidadesSeleccion());
  }

  toggleSearch(): void {
    this.showSearchPanel = !this.showSearchPanel;
    if (!this.showSearchPanel) {
      this.resetFilters();
    }
  }

  searchModalidadesSeleccion(query: string): void {
    this.searchQuery = query;
    this.applyFilters();
  }

  onColumnFilterChange(field: keyof typeof this.columnFilters, value: string): void {
    this.columnFilters[field] = value;
    this.applyFilters();
  }

  resetFilters(): void {
    this.clearFilters();
  }

  openCreateModal(modalidad?: ModalidadSeleccion): void {
    this.isEditing = !!modalidad;
    this.editingModalidadSeleccion = modalidad || null;

    if (modalidad) {
      this.modalidadSeleccionForm.patchValue({
        codigo: modalidad.codigo,
        caracteristicas: modalidad.caracteristicas,
        estado: modalidad.estado
      });
    } else {
      this.modalidadSeleccionForm.reset({
        codigo: '',
        caracteristicas: '',
        estado: true
      });
    }

    this.dialog.open(this.createModalidadSeleccionModal, {
      width: '500px',
      disableClose: true
    });
  }

  closeModal(): void {
    this.dialog.closeAll();
    this.resetForm();
  }

  async saveModalidadSeleccion(): Promise<void> {
    if (this.modalidadSeleccionForm.valid) {
      const formData = this.modalidadSeleccionForm.value;

      if (this.isEditing && this.editingModalidadSeleccion) {
        // Para editar, mostrar confirmación primero
        const confirmed = await this.notificationService.showConfirmation(
          '¿Está seguro de que desea actualizar esta modalidad de selección?',
          'Confirmar Actualización'
        );

        if (confirmed) {
          const updateRequest: UpdateModalidadSeleccionRequest = {
            Codigo: formData.codigo,
            Caracteristicas: formData.caracteristicas
          };
          this.store.dispatch(
            ModalidadSeleccionActions.updateModalidadSeleccion({
              id: this.editingModalidadSeleccion.modalidadSelId,
              modalidadSeleccion: updateRequest
            })
          );

          // Cerrar modal después de actualizar
          this.closeModal();
          this.cdr.detectChanges();
        }
      } else {
        // Para crear, mostrar confirmación primero
        const confirmed = await this.notificationService.showConfirmation(
          '¿Está seguro de que desea crear esta modalidad de selección?',
          'Confirmar Creación'
        );

        if (confirmed) {
          const createRequest: CreateModalidadSeleccionRequest = {
            Codigo: formData.codigo,
            Caracteristicas: formData.caracteristicas,
            Estado: formData.estado
          };
          this.store.dispatch(
            ModalidadSeleccionActions.createModalidadSeleccion({
              modalidadSeleccion: createRequest
            })
          );

          // Cerrar modal después de crear
          this.closeModal();
          this.cdr.detectChanges();
        }
      }
    } else {
      this.markFormGroupTouched();
    }
  }

  viewDetail(modalidad: ModalidadSeleccion): void {
    this.detailBeingViewed = modalidad;
    this.dialog.open(this.detailModal, {
      width: '500px',
      disableClose: false
    });
  }

  async toggleModalidadSeleccionStatus(modalidad: ModalidadSeleccion): Promise<void> {
    const newStatus = !modalidad.estado;
    const action = newStatus ? 'activar' : 'desactivar';

    const confirmed = await this.notificationService.showConfirmation(
      `¿Está seguro de que desea ${action} esta modalidad de selección?`,
      'Confirmar cambio de estado'
    );

    if (confirmed) {
      this.store.dispatch(
        ModalidadSeleccionActions.toggleModalidadSeleccionStatus({
          id: modalidad.modalidadSelId,
          isActive: newStatus
        })
      );

      // El store se actualiza con la respuesta del efecto; no recargamos manualmente
    }
  }

  hasError(controlName: string): boolean {
    const control = this.modalidadSeleccionForm.get(controlName);
    return !!(control?.invalid && control?.touched);
  }

  getErrorMessage(controlName: string): string {
    const control = this.modalidadSeleccionForm.get(controlName);

    if (control?.hasError('required')) {
      switch (controlName) {
        case 'codigo':
          return 'El código es obligatorio';
        case 'caracteristicas':
          return 'Las características son obligatorias';
        default:
          return 'Este campo es obligatorio';
      }
    }

    if (control?.hasError('minlength')) {
      switch (controlName) {
        case 'codigo':
          return 'El código debe tener al menos 2 caracteres';
        case 'caracteristicas':
          return 'Las características deben tener al menos 5 caracteres';
        default:
          return 'Longitud mínima no alcanzada';
      }
    }

    if (control?.hasError('maxlength')) {
      switch (controlName) {
        case 'codigo':
          return 'El código no puede tener más de 50 caracteres';
        case 'caracteristicas':
          return 'Las características no pueden tener más de 500 caracteres';
        default:
          return 'Longitud máxima excedida';
      }
    }

    return '';
  }

  /**
   * Marca todos los campos del formulario como touched
   */
  private markFormGroupTouched(): void {
    Object.keys(this.modalidadSeleccionForm.controls).forEach(key => {
      const control = this.modalidadSeleccionForm.get(key);
      control?.markAsTouched();
    });
  }

  /**
   * Resetea el formulario
   */
  private resetForm(): void {
    this.modalidadSeleccionForm.reset({
      codigo: '',
      caracteristicas: '',
      estado: true
    });
    this.isEditing = false;
    this.editingModalidadSeleccion = null;
  }

  private subscribeToError(): void {
    this.error$.pipe(take(1)).subscribe(error => {
      if (error) {
        this.errorHandler.handleError(error);
      }
    });

    // Suscripción a creación exitosa
    this.creating$.pipe(takeUntil(this.destroy$)).subscribe(creating => {
      if (!creating && this.wasCreating) {
        this.notificationService.showSuccessModal('Modalidad de selección creada exitosamente');
      }
      this.wasCreating = creating as boolean;
    });

    // Suscripción a actualización exitosa
    this.updating$.pipe(takeUntil(this.destroy$)).subscribe(updating => {
      if (!updating && this.wasUpdating) {
        this.notificationService.showSuccessModal(
          'Modalidad de selección actualizada exitosamente'
        );
      }
      this.wasUpdating = updating as boolean;
    });
  }
}
